/**
 * Admin Settings Component
 * Provides interface for managing application settings
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Alert,
    Divider,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
} from '@mui/material';
import {
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    RestartAlt as ResetIcon,
    Security as SecurityIcon,
    Settings as SettingsIcon,
    Info as InfoIcon,
} from '@mui/icons-material';

interface AdminSettingsProps {
    onSave?: (settings: any) => void;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ onSave }) => {
    const [settings, setSettings] = useState({
        security: {
            enableTwoFactor: true,
            sessionTimeout: 30,
            maxLoginAttempts: 5,
            passwordMinLength: 8,
            requireUppercase: true,
            requireLowercase: true,
            requireNumbers: true,
            requireSpecialChars: true,
            enableRateLimiting: true,
            rateLimitRequests: 100,
            rateLimitWindow: 60,
        },
        system: {
            appName: 'P2P Exchange',
            maxConcurrentUsers: 1000,
            logLevel: 'info' as 'debug' | 'info' | 'warn' | 'error',
            debugMode: false,
            dataRetentionDays: 90,
            maintenanceMode: false,
            enableAnalytics: true,
            autoBackup: true,
            backupInterval: 24,
        },
        notifications: {
            emailNotifications: true,
            securityAlerts: true,
            systemAlerts: true,
            attackAlerts: true,
            adminEmail: 'admin@p2pexchange.com',
        },
        performance: {
            enableCaching: true,
            cacheTimeout: 300,
            enableCompression: true,
            maxRequestSize: 10,
        },
    });

    const [hasChanges, setHasChanges] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);

    const handleSettingChange = useCallback((category: string, key: string, value: any) => {
        setSettings(prev => ({
            ...prev,
            [category]: {
                ...prev[category as keyof typeof prev],
                [key]: value,
            },
        }));
        setHasChanges(true);
    }, []);

    const handleSave = useCallback(() => {
        try {
            // Save to localStorage for persistence
            localStorage.setItem('adminSettings', JSON.stringify(settings));
            
            if (onSave) {
                onSave(settings);
            }
            
            setSuccess('Settings saved successfully!');
            setHasChanges(false);
            
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Failed to save settings');
            setTimeout(() => setError(null), 3000);
        }
    }, [settings, onSave]);

    const handleReset = useCallback(() => {
        // Reset to defaults
        const defaultSettings = {
            security: {
                enableTwoFactor: true,
                sessionTimeout: 30,
                maxLoginAttempts: 5,
                passwordMinLength: 8,
                requireUppercase: true,
                requireLowercase: true,
                requireNumbers: true,
                requireSpecialChars: true,
                enableRateLimiting: true,
                rateLimitRequests: 100,
                rateLimitWindow: 60,
            },
            system: {
                appName: 'P2P Exchange',
                maxConcurrentUsers: 1000,
                logLevel: 'info' as 'debug' | 'info' | 'warn' | 'error',
                debugMode: false,
                dataRetentionDays: 90,
                maintenanceMode: false,
                enableAnalytics: true,
                autoBackup: true,
                backupInterval: 24,
            },
            notifications: {
                emailNotifications: true,
                securityAlerts: true,
                systemAlerts: true,
                attackAlerts: true,
                adminEmail: 'admin@p2pexchange.com',
            },
            performance: {
                enableCaching: true,
                cacheTimeout: 300,
                enableCompression: true,
                maxRequestSize: 10,
            },
        };
        
        setSettings(defaultSettings);
        setHasChanges(true);
        setResetDialogOpen(false);
        setSuccess('Settings reset to defaults');
        setTimeout(() => setSuccess(null), 3000);
    }, []);

    const handleExport = useCallback(() => {
        const dataStr = JSON.stringify(settings, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `admin-settings-${new Date().toISOString()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [settings]);

    const handleLoad = useCallback(() => {
        try {
            const saved = localStorage.getItem('adminSettings');
            if (saved) {
                setSettings(JSON.parse(saved));
                setSuccess('Settings loaded from storage');
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            setError('Failed to load settings');
            setTimeout(() => setError(null), 3000);
        }
    }, []);

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Admin Settings
                </Typography>
                <Box display="flex" gap={1}>
                    <Tooltip title="Load from storage">
                        <IconButton onClick={handleLoad}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export settings">
                        <IconButton onClick={handleExport}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset to defaults">
                        <IconButton onClick={() => setResetDialogOpen(true)} color="warning">
                            <ResetIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={!hasChanges}
                    >
                        Save Changes
                    </Button>
                </Box>
            </Box>

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {hasChanges && (
                <Alert severity="warning" sx={{ mb: 2 }} icon={<InfoIcon />}>
                    You have unsaved changes. Click "Save Changes" to apply them.
                </Alert>
            )}

            {/* Security Settings */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <SecurityIcon color="primary" />
                        <Typography variant="h6">Security Settings</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Stack spacing={3}>
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.security.enableTwoFactor}
                                        onChange={(e) => handleSettingChange('security', 'enableTwoFactor', e.target.checked)}
                                    />
                                }
                                label="Enable Two-Factor Authentication"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.security.enableRateLimiting}
                                        onChange={(e) => handleSettingChange('security', 'enableRateLimiting', e.target.checked)}
                                    />
                                }
                                label="Enable Rate Limiting"
                            />
                        </Box>

                        <Box display="flex" gap={2} flexWrap="wrap">
                            <TextField
                                sx={{ flex: '1 1 200px' }}
                                label="Session Timeout (minutes)"
                                type="number"
                                value={settings.security.sessionTimeout}
                                onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
                                helperText="Auto-logout after inactivity"
                            />
                            <TextField
                                sx={{ flex: '1 1 200px' }}
                                label="Max Login Attempts"
                                type="number"
                                value={settings.security.maxLoginAttempts}
                                onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                                helperText="Before account lockout"
                            />
                        </Box>

                        <Box display="flex" gap={2} flexWrap="wrap">
                            <TextField
                                sx={{ flex: '1 1 200px' }}
                                label="Password Min Length"
                                type="number"
                                value={settings.security.passwordMinLength}
                                onChange={(e) => handleSettingChange('security', 'passwordMinLength', parseInt(e.target.value))}
                            />
                            <TextField
                                sx={{ flex: '1 1 200px' }}
                                label="Rate Limit (requests/window)"
                                type="number"
                                value={settings.security.rateLimitRequests}
                                onChange={(e) => handleSettingChange('security', 'rateLimitRequests', parseInt(e.target.value))}
                                disabled={!settings.security.enableRateLimiting}
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Password Requirements</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={settings.security.requireUppercase}
                                            onChange={(e) => handleSettingChange('security', 'requireUppercase', e.target.checked)}
                                        />
                                    }
                                    label="Uppercase"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={settings.security.requireLowercase}
                                            onChange={(e) => handleSettingChange('security', 'requireLowercase', e.target.checked)}
                                        />
                                    }
                                    label="Lowercase"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={settings.security.requireNumbers}
                                            onChange={(e) => handleSettingChange('security', 'requireNumbers', e.target.checked)}
                                        />
                                    }
                                    label="Numbers"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={settings.security.requireSpecialChars}
                                            onChange={(e) => handleSettingChange('security', 'requireSpecialChars', e.target.checked)}
                                        />
                                    }
                                    label="Special Characters"
                                />
                            </Box>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* System Settings */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <SettingsIcon color="primary" />
                        <Typography variant="h6">System Settings</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Stack spacing={3}>
                        <Box sx={{ flex: '1 1 200px' }}>
                            <TextField
                                fullWidth
                                label="Application Name"
                                value={settings.system.appName}
                                onChange={(e) => handleSettingChange('system', 'appName', e.target.value)}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 200px' }}>
                            <FormControl fullWidth>
                                <InputLabel>Log Level</InputLabel>
                                <Select
                                    value={settings.system.logLevel}
                                    label="Log Level"
                                    onChange={(e) => handleSettingChange('system', 'logLevel', e.target.value)}
                                >
                                    <MenuItem value="debug">Debug</MenuItem>
                                    <MenuItem value="info">Info</MenuItem>
                                    <MenuItem value="warn">Warning</MenuItem>
                                    <MenuItem value="error">Error</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: '1 1 200px' }}>
                            <TextField
                                fullWidth
                                label="Max Concurrent Users"
                                type="number"
                                value={settings.system.maxConcurrentUsers}
                                onChange={(e) => handleSettingChange('system', 'maxConcurrentUsers', parseInt(e.target.value))}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 200px' }}>
                            <TextField
                                fullWidth
                                label="Data Retention (days)"
                                type="number"
                                value={settings.system.dataRetentionDays}
                                onChange={(e) => handleSettingChange('system', 'dataRetentionDays', parseInt(e.target.value))}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 200px' }}>
                            <TextField
                                fullWidth
                                label="Auto Backup Interval (hours)"
                                type="number"
                                value={settings.system.backupInterval}
                                onChange={(e) => handleSettingChange('system', 'backupInterval', parseInt(e.target.value))}
                                disabled={!settings.system.autoBackup}
                            />
                        </Box>

                        <Box>
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.system.debugMode}
                                            onChange={(e) => handleSettingChange('system', 'debugMode', e.target.checked)}
                                        />
                                    }
                                    label="Debug Mode"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.system.maintenanceMode}
                                            onChange={(e) => handleSettingChange('system', 'maintenanceMode', e.target.checked)}
                                        />
                                    }
                                    label="Maintenance Mode"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.system.enableAnalytics}
                                            onChange={(e) => handleSettingChange('system', 'enableAnalytics', e.target.checked)}
                                        />
                                    }
                                    label="Enable Analytics"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.system.autoBackup}
                                            onChange={(e) => handleSettingChange('system', 'autoBackup', e.target.checked)}
                                        />
                                    }
                                    label="Auto Backup"
                                />
                            </Box>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* Notification Settings */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Notification Settings</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Stack spacing={3}>
                        <Box sx={{ flex: '1 1 200px' }}>
                            <TextField
                                fullWidth
                                label="Admin Email"
                                type="email"
                                value={settings.notifications.adminEmail}
                                onChange={(e) => handleSettingChange('notifications', 'adminEmail', e.target.value)}
                            />
                        </Box>

                        <Box>
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.notifications.emailNotifications}
                                            onChange={(e) => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
                                        />
                                    }
                                    label="Email Notifications"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.notifications.securityAlerts}
                                            onChange={(e) => handleSettingChange('notifications', 'securityAlerts', e.target.checked)}
                                        />
                                    }
                                    label="Security Alerts"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.notifications.systemAlerts}
                                            onChange={(e) => handleSettingChange('notifications', 'systemAlerts', e.target.checked)}
                                        />
                                    }
                                    label="System Alerts"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.notifications.attackAlerts}
                                            onChange={(e) => handleSettingChange('notifications', 'attackAlerts', e.target.checked)}
                                        />
                                    }
                                    label="Attack Alerts"
                                />
                            </Box>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* Performance Settings */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" gutterBottom>Performance Settings</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Stack spacing={3}>
                        <Box sx={{ flex: '1 1 200px' }}>
                            <TextField
                                fullWidth
                                label="Cache Timeout (seconds)"
                                type="number"
                                value={settings.performance.cacheTimeout}
                                onChange={(e) => handleSettingChange('performance', 'cacheTimeout', parseInt(e.target.value))}
                                disabled={!settings.performance.enableCaching}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 200px' }}>
                            <TextField
                                fullWidth
                                label="Max Request Size (MB)"
                                type="number"
                                value={settings.performance.maxRequestSize}
                                onChange={(e) => handleSettingChange('performance', 'maxRequestSize', parseInt(e.target.value))}
                            />
                        </Box>

                        <Box>
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.performance.enableCaching}
                                            onChange={(e) => handleSettingChange('performance', 'enableCaching', e.target.checked)}
                                        />
                                    }
                                    label="Enable Caching"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={settings.performance.enableCompression}
                                            onChange={(e) => handleSettingChange('performance', 'enableCompression', e.target.checked)}
                                        />
                                    }
                                    label="Enable Compression"
                                />
                            </Box>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* Reset Confirmation Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
                <DialogTitle>Reset Settings to Defaults?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will reset all settings to their default values. Any unsaved changes will be lost.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleReset} color="warning" variant="contained">
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminSettings;
